import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';
import {
  Plus, Trash2, Edit2, Search, Eye, X, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, CheckSquare, EyeOff, CheckCircle2,
  SlidersHorizontal, RotateCcw, ChevronDown, FileQuestion, BookOpen,
  Layers, Filter
} from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const C = COLORS;

const StatCard = ({ title, value, subtitle, icon: Icon, color, bg, border, isActive, onClick, activeBadgeText }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 16,
        border: isActive ? `2px solid ${color}` : `1px solid ${hovered ? color : (border || C.g200)}`,
        padding: '18px 20px',
        boxShadow: isActive
          ? `0 10px 25px -5px ${color}35, 0 4px 10px rgba(0,0,0,0.03)`
          : (hovered ? '0 10px 24px -4px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)'),
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        userSelect: 'none',
      }}
    >
      {isActive && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: color,
        }} />
      )}

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.g500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {title}
            </span>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.g900, marginTop: 4, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {typeof value === 'number' ? value.toLocaleString() : value || 0}
            </div>
          </div>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: bg || '#F3F4F6',
            color: color || C.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={22} />
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 10,
        borderTop: `1px solid ${C.g100}`,
        marginTop: 6
      }}>
        <span style={{ fontSize: 12, color: C.g600, fontWeight: 500 }}>
          {subtitle}
        </span>
        {isActive ? (
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: color,
            background: bg,
            padding: '2px 8px',
            borderRadius: 10,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4
          }}>
            ● {activeBadgeText || 'Filtered'}
          </span>
        ) : (
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            color: hovered ? color : C.g400,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            transition: 'color 0.15s ease'
          }}>
            Filter <ChevronRight size={13} />
          </span>
        )}
      </div>
    </div>
  );
};

const QuestionList = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isOwnQuestion = (q) => q && Number(q.created_by) === Number(user?.id);
  const canManageQuestion = (q) => isOwnQuestion(q);

  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Configurations & Filters
  const [boards, setBoards] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);

  const [questionTypeFilter, setQuestionTypeFilter] = useState('');
  const [boardFilter, setBoardFilter] = useState('');
  const [examFilter, setExamFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [subTopicFilter, setSubTopicFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Advance filter panel toggle
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);

  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // View/Edit Modals
  const [viewQuestion, setViewQuestion] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchQuestions();
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const [b, ex, c, s, t, st] = await Promise.all([
        api.get('/config/board'),
        api.get('/config/exam'),
        api.get('/config/class'),
        api.get('/config/subject'),
        api.get('/config/topic').catch(() => ({ data: [] })),
        api.get('/config/sub-topic').catch(() => ({ data: [] }))
      ]);
      setBoards(b.data.filter(x => x.status === 'active'));
      setExams(ex.data.filter(x => x.status === 'active'));
      setClasses(c.data.filter(x => x.status === 'active'));
      setSubjects(s.data.filter(x => x.status === 'active'));
      if (Array.isArray(t?.data)) setTopics(t.data.filter(x => x.status === 'active'));
      if (Array.isArray(st?.data)) setSubTopics(st.data.filter(x => x.status === 'active'));
    } catch (err) {
      console.error('Failed to load configs', err);
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const res = await api.get('/questions');
      setQuestions(res.data);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const q = questions.find(x => x.id === id);
    if (!q || !isOwnQuestion(q)) {
      toast.error('You can only delete questions you uploaded or created.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await api.delete(`/questions/${id}`);
      toast.success('Question deleted successfully');
      setSelectedIds(prev => prev.filter(x => x !== id));
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete question');
    }
  };

  const handleToggleStatus = async (q) => {
    if (!canManageQuestion(q)) {
      toast.error('You can only modify questions you added.');
      return;
    }
    const currentStatus = q.status || 'active';
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    const actionLabel = newStatus === 'inactive' ? 'Deactivating' : 'Activating';
    const toastId = toast.loading(`${actionLabel} question...`);
    try {
      await api.patch(`/questions/${q.id}/status`, { status: newStatus });
      toast.success(`Question ${newStatus === 'inactive' ? 'deactivated' : 'activated'} successfully`, { id: toastId });
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status', { id: toastId });
    }
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    if (editQuestion && !canManageQuestion(editQuestion)) {
      toast.error('You can only edit questions you added.');
      return;
    }
    try {
      const toastId = toast.loading('Updating question...');
      await api.put(`/questions/${editQuestion.id}`, editQuestion);
      toast.success('Question updated successfully', { id: toastId });
      setEditQuestion(null);
      fetchQuestions();
    } catch (err) {
      toast.error('Failed to update question');
    }
  };

  // Bulk Operations
  const handleBulkStatus = async (status) => {
    if (selectedIds.length === 0) return;
    const isDeactivate = status === 'inactive';
    const actionVerb = isDeactivate ? 'deactivate' : 'activate';
    
    if (!window.confirm(`Are you sure you want to ${actionVerb} ${selectedIds.length} selected question(s)?`)) {
      return;
    }

    setBulkLoading(true);
    const toastId = toast.loading(`${isDeactivate ? 'Deactivating' : 'Activating'} ${selectedIds.length} question(s)...`);
    try {
      const res = await api.post('/questions/bulk-status', { ids: selectedIds, status });
      toast.success(res.data?.message || `${selectedIds.length} questions updated`, { id: toastId });
      setSelectedIds([]);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${actionVerb} questions`, { id: toastId });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.length} selected question(s)? This action cannot be undone.`)) {
      return;
    }

    setBulkLoading(true);
    const toastId = toast.loading(`Deleting ${selectedIds.length} question(s)...`);
    try {
      const res = await api.post('/questions/bulk-delete', { ids: selectedIds });
      toast.success(res.data?.message || `${selectedIds.length} questions deleted`, { id: toastId });
      setSelectedIds([]);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete questions', { id: toastId });
    } finally {
      setBulkLoading(false);
    }
  };

  // Merge topics from API and questions so user always has complete topic list
  const combinedTopics = React.useMemo(() => {
    const map = new Map();
    topics.forEach(t => map.set(String(t.id), { id: t.id, name: t.name, board_id: t.board_id, exam_id: t.exam_id, class_id: t.class_id, subject_id: t.subject_id }));
    questions.forEach(q => {
      if (q.topic_id && q.topic_name && !map.has(String(q.topic_id))) {
        map.set(String(q.topic_id), { id: q.topic_id, name: q.topic_name, board_id: q.board_id, exam_id: q.exam_id, class_id: q.class_id, subject_id: q.subject_id });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [topics, questions]);

  const combinedSubTopics = React.useMemo(() => {
    const map = new Map();
    subTopics.forEach(st => map.set(String(st.id), { id: st.id, name: st.name, topic_id: st.topic_id }));
    questions.forEach(q => {
      if (q.sub_topic_id && q.sub_topic_name && !map.has(String(q.sub_topic_id))) {
        map.set(String(q.sub_topic_id), { id: q.sub_topic_id, name: q.sub_topic_name, topic_id: q.topic_id });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [subTopics, questions]);

  const availableTopics = React.useMemo(() => {
    return combinedTopics.filter(t => {
      if (questionTypeFilter === 'Board Exams' && t.exam_id) return false;
      if (questionTypeFilter === 'Compititive' && t.board_id) return false;
      if (boardFilter && t.board_id && String(t.board_id) !== String(boardFilter)) return false;
      if (examFilter && t.exam_id && String(t.exam_id) !== String(examFilter)) return false;
      if (classFilter && t.class_id && String(t.class_id) !== String(classFilter)) return false;
      if (subjectFilter && t.subject_id && String(t.subject_id) !== String(subjectFilter)) return false;
      return true;
    });
  }, [combinedTopics, questionTypeFilter, boardFilter, examFilter, classFilter, subjectFilter]);

  const availableSubTopics = React.useMemo(() => {
    return combinedSubTopics.filter(st => {
      if (topicFilter && st.topic_id && String(st.topic_id) !== String(topicFilter)) return false;
      return true;
    });
  }, [combinedSubTopics, topicFilter]);

  // Metric counts and filtering
  const categoryQuestions = questions.filter(q => {
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      const matchSearch = (
        (q.question_text || '').toLowerCase().includes(term) ||
        (q.topic_name || '').toLowerCase().includes(term) ||
        (q.sub_topic_name || '').toLowerCase().includes(term) ||
        (q.subject_name || '').toLowerCase().includes(term) ||
        (q.board_name || '').toLowerCase().includes(term) ||
        (q.exam_name || '').toLowerCase().includes(term) ||
        (q.class_name || '').toLowerCase().includes(term) ||
        (q.level || '').toLowerCase().includes(term) ||
        String(q.id).includes(term)
      );
      if (!matchSearch) return false;
    }
    
    const qExamId = q.exam_id || q.topic_exam_id;
    const isCompetitive = Boolean(qExamId);

    if (questionTypeFilter === 'Board Exams') {
      if (isCompetitive || !q.board_id) return false;
    }
    if (questionTypeFilter === 'Compititive') {
      if (!isCompetitive) return false;
    }
    
    if (questionTypeFilter !== 'Compititive' && boardFilter && String(q.board_id) !== String(boardFilter)) return false;
    if (questionTypeFilter === 'Compititive' && examFilter && String(qExamId) !== String(examFilter)) return false;
    if (!questionTypeFilter) {
      if (boardFilter && String(q.board_id) !== String(boardFilter)) return false;
      if (examFilter && String(qExamId) !== String(examFilter)) return false;
    }
    
    if (classFilter && String(q.class_id) !== String(classFilter)) return false;
    if (subjectFilter && String(q.subject_id) !== String(subjectFilter)) return false;
    if (topicFilter && String(q.topic_id) !== String(topicFilter)) return false;
    if (subTopicFilter && String(q.sub_topic_id) !== String(subTopicFilter)) return false;
    if (levelFilter && String(q.level) !== String(levelFilter)) return false;
    return true;
  });

  const filtered = categoryQuestions.filter(q => {
    if (statusFilter && (q.status || 'active') !== statusFilter) return false;
    return true;
  });

  // Dynamic box counts calculated from current filter criteria!
  const boxTotalQuestions = categoryQuestions.length;
  const boxActiveQuestions = categoryQuestions.filter(q => (q.status || 'active') === 'active').length;
  const boxInactiveQuestions = categoryQuestions.filter(q => q.status === 'inactive').length;
  const boxTopicsCount = new Set(categoryQuestions.map(q => q.topic_id || q.topic_name).filter(Boolean)).size;
  const boxSubTopicsCount = new Set(categoryQuestions.map(q => q.sub_topic_id || q.sub_topic_name).filter(Boolean)).size;

  const activeFilters = [];
  if (questionTypeFilter) {
    activeFilters.push({
      key: 'type',
      label: `Type: ${questionTypeFilter === 'Compititive' ? 'Competitive Exams' : questionTypeFilter}`,
      clear: () => { setQuestionTypeFilter(''); setBoardFilter(''); setExamFilter(''); setCurrentPage(1); }
    });
  }
  if (boardFilter) {
    const b = boards.find(x => String(x.id) === String(boardFilter));
    activeFilters.push({
      key: 'board',
      label: `Board: ${b?.name || boardFilter}`,
      clear: () => { setBoardFilter(''); setCurrentPage(1); }
    });
  }
  if (examFilter) {
    const ex = exams.find(x => String(x.id) === String(examFilter));
    activeFilters.push({
      key: 'exam',
      label: `Exam: ${ex?.name || examFilter}`,
      clear: () => { setExamFilter(''); setCurrentPage(1); }
    });
  }
  if (classFilter) {
    const c = classes.find(x => String(x.id) === String(classFilter));
    activeFilters.push({
      key: 'class',
      label: `Class: ${c?.name || classFilter}`,
      clear: () => { setClassFilter(''); setCurrentPage(1); }
    });
  }
  if (subjectFilter) {
    const s = subjects.find(x => String(x.id) === String(subjectFilter));
    activeFilters.push({
      key: 'subject',
      label: `Subject: ${s?.name || subjectFilter}`,
      clear: () => { setSubjectFilter(''); setCurrentPage(1); }
    });
  }
  if (topicFilter) {
    const t = combinedTopics.find(x => String(x.id) === String(topicFilter));
    activeFilters.push({
      key: 'topic',
      label: `Topic: ${t?.name || topicFilter}`,
      clear: () => { setTopicFilter(''); setCurrentPage(1); }
    });
  }
  if (subTopicFilter) {
    const st = combinedSubTopics.find(x => String(x.id) === String(subTopicFilter));
    activeFilters.push({
      key: 'subTopic',
      label: `Sub Topic: ${st?.name || subTopicFilter}`,
      clear: () => { setSubTopicFilter(''); setCurrentPage(1); }
    });
  }
  if (levelFilter) {
    activeFilters.push({
      key: 'level',
      label: `Level: ${levelFilter}`,
      clear: () => { setLevelFilter(''); setCurrentPage(1); }
    });
  }
  if (statusFilter) {
    activeFilters.push({
      key: 'status',
      label: `Status: ${statusFilter === 'active' ? 'Active' : 'Inactive'}`,
      clear: () => { setStatusFilter(''); setCurrentPage(1); }
    });
  }
  if (searchTerm) {
    activeFilters.push({
      key: 'search',
      label: `Search: "${searchTerm}"`,
      clear: () => { setSearchTerm(''); setCurrentPage(1); }
    });
  }

  const handleResetAllFilters = () => {
    setSearchTerm('');
    setQuestionTypeFilter('');
    setBoardFilter('');
    setExamFilter('');
    setClassFilter('');
    setSubjectFilter('');
    setTopicFilter('');
    setSubTopicFilter('');
    setLevelFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const isAllFilterDefault = !statusFilter && !topicFilter && !subTopicFilter && !levelFilter && !questionTypeFilter && !boardFilter && !examFilter && !classFilter && !subjectFilter && !searchTerm;

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Manageable questions on page
  const manageablePaginated = paginated.filter(q => canManageQuestion(q));
  const isAllPageSelected = manageablePaginated.length > 0 && manageablePaginated.every(q => selectedIds.includes(q.id));
  const isSomePageSelected = manageablePaginated.some(q => selectedIds.includes(q.id)) && !isAllPageSelected;

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllPage = () => {
    if (isAllPageSelected) {
      // Uncheck all manageable on this page
      const pageIds = manageablePaginated.map(q => q.id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      // Check all manageable on this page
      const pageIds = manageablePaginated.map(q => q.id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleSelectAllFiltered = () => {
    const allFilteredManageableIds = filtered.filter(q => canManageQuestion(q)).map(q => q.id);
    setSelectedIds(allFilteredManageableIds);
  };

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const filterLabelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: C.g600,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  };

  const filterSelectStyle = (isActive) => ({
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: `1.5px solid ${isActive ? C.primary : C.g200}`,
    background: isActive ? '#EEF2FF' : '#ffffff',
    color: isActive ? '#1E1B4B' : C.g800,
    fontWeight: isActive ? 600 : 400,
    fontSize: 13,
    outline: 'none',
    transition: 'all 0.15s ease',
    cursor: 'pointer',
  });

  return (
    <AppLayout title="Questions Management">
      {/* ─── 1. Search & Advance Filter Action Bar (Above the boxes) ─── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, flexWrap: 'wrap', minWidth: 280 }}>
          {/* Main Search Input */}
          <div style={{ position: 'relative', width: 340, maxWidth: '100%' }}>
            <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: 12 }} />
            <input
              type="text"
              placeholder="Search questions by text, topic, level..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              style={{
                width: '100%',
                padding: '11px 36px 11px 40px',
                borderRadius: 12,
                border: `1.5px solid ${searchTerm ? C.primary : C.g200}`,
                fontSize: 14,
                outline: 'none',
                background: '#fff',
                boxShadow: searchTerm ? '0 0 0 3px rgba(79,70,229,0.1)' : '0 1px 2px rgba(0,0,0,0.03)',
                transition: 'all 0.15s ease'
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 11,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: C.g400,
                  padding: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Advance Filters Toggle Button */}
          <button
            type="button"
            onClick={() => setShowAdvanceFilters(prev => !prev)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '11px 18px',
              borderRadius: 12,
              border: `1.5px solid ${showAdvanceFilters || activeFilters.length > 0 ? C.primary : C.g200}`,
              background: showAdvanceFilters ? C.primaryGhost : (activeFilters.length > 0 ? '#EEF2FF' : '#fff'),
              color: showAdvanceFilters || activeFilters.length > 0 ? C.primary : C.g700,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
              transition: 'all 0.15s ease'
            }}
          >
            <SlidersHorizontal size={17} />
            <span>Advance Filters</span>
            {activeFilters.length > 0 && (
              <span style={{
                background: C.primary,
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 10,
                padding: '1px 7px',
                marginLeft: 2
              }}>
                {activeFilters.length}
              </span>
            )}
            <ChevronDown size={16} style={{ transform: showAdvanceFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
          </button>

          {/* Reset All Filters button */}
          {(activeFilters.length > 0 || searchTerm) && (
            <button
              type="button"
              onClick={handleResetAllFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '11px 14px',
                borderRadius: 12,
                border: '1px solid #FCA5A5',
                background: '#FEF2F2',
                color: '#DC2626',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              title="Reset all search and advance filters"
            >
              <RotateCcw size={14} />
              <span>Reset</span>
            </button>
          )}
        </div>

        {/* Add Questions Button */}
        <button
          onClick={() => navigate('/config/questions/add')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '11px 18px',
            background: C.primary,
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(79,70,229,0.25)',
            transition: 'all 0.15s ease'
          }}
        >
          <Plus size={18} /> Add Questions
        </button>
      </div>

      {/* ─── 2. Collapsible Advance Filter Panel ─── */}
      {showAdvanceFilters && (
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          border: `1.5px solid ${C.g200}`,
          padding: '20px 24px',
          marginBottom: 20,
          boxShadow: '0 8px 24px -4px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.02)',
        }}>
          {/* Panel Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18,
            paddingBottom: 14,
            borderBottom: `1px solid ${C.g100}`,
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: C.primaryGhost,
                color: C.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SlidersHorizontal size={18} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.g900 }}>Advance Filter Options</h3>
                <span style={{ fontSize: 12, color: C.g500 }}>
                  Showing {filtered.length} of {questions.length} total questions in question bank
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {activeFilters.length > 0 && (
                <button
                  type="button"
                  onClick={handleResetAllFilters}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '7px 12px',
                    background: '#FEF2F2',
                    color: '#DC2626',
                    border: '1px solid #FECACA',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <RotateCcw size={13} /> Reset Filters
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowAdvanceFilters(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '7px 12px',
                  background: C.g100,
                  color: C.g700,
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <X size={15} /> Close
              </button>
            </div>
          </div>

          {/* Filter Inputs Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16 }}>
            {/* 1. Question Type */}
            <div>
              <label style={filterLabelStyle}>Question Type</label>
              <select
                value={questionTypeFilter}
                onChange={e => {
                  setQuestionTypeFilter(e.target.value);
                  setBoardFilter('');
                  setExamFilter('');
                  setTopicFilter('');
                  setSubTopicFilter('');
                  setCurrentPage(1);
                }}
                style={filterSelectStyle(Boolean(questionTypeFilter))}
              >
                <option value="">All Question Types</option>
                <option value="Board Exams">Board Exams</option>
                <option value="Compititive">Competitive Exams</option>
              </select>
            </div>

            {/* 2. Board (shown if not Competitive) */}
            {questionTypeFilter !== 'Compititive' && (
              <div>
                <label style={filterLabelStyle}>Board</label>
                <select
                  value={boardFilter}
                  onChange={e => {
                    setBoardFilter(e.target.value);
                    setTopicFilter('');
                    setSubTopicFilter('');
                    setCurrentPage(1);
                  }}
                  style={filterSelectStyle(Boolean(boardFilter))}
                >
                  <option value="">All Boards</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            {/* 3. Exam (shown if Competitive or All) */}
            {questionTypeFilter !== 'Board Exams' && (
              <div>
                <label style={filterLabelStyle}>Competitive Exam</label>
                <select
                  value={examFilter}
                  onChange={e => {
                    setExamFilter(e.target.value);
                    setTopicFilter('');
                    setSubTopicFilter('');
                    setCurrentPage(1);
                  }}
                  style={filterSelectStyle(Boolean(examFilter))}
                >
                  <option value="">All Exams</option>
                  {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
            )}

            {/* 4. Class */}
            <div>
              <label style={filterLabelStyle}>Class / Grade</label>
              <select
                value={classFilter}
                onChange={e => {
                  setClassFilter(e.target.value);
                  setTopicFilter('');
                  setSubTopicFilter('');
                  setCurrentPage(1);
                }}
                style={filterSelectStyle(Boolean(classFilter))}
              >
                <option value="">All Classes</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* 5. Subject */}
            <div>
              <label style={filterLabelStyle}>Subject</label>
              <select
                value={subjectFilter}
                onChange={e => {
                  setSubjectFilter(e.target.value);
                  setTopicFilter('');
                  setSubTopicFilter('');
                  setCurrentPage(1);
                }}
                style={filterSelectStyle(Boolean(subjectFilter))}
              >
                <option value="">All Subjects</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* 6. Topic */}
            <div>
              <label style={filterLabelStyle}>Topic</label>
              <select
                value={topicFilter}
                onChange={e => {
                  setTopicFilter(e.target.value);
                  setSubTopicFilter('');
                  setCurrentPage(1);
                }}
                style={filterSelectStyle(Boolean(topicFilter))}
              >
                <option value="">All Topics ({availableTopics.length})</option>
                {availableTopics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>

            {/* 7. Sub Topic */}
            <div>
              <label style={filterLabelStyle}>Sub Topic</label>
              <select
                value={subTopicFilter}
                onChange={e => {
                  setSubTopicFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={filterSelectStyle(Boolean(subTopicFilter))}
              >
                <option value="">All Sub Topics ({availableSubTopics.length})</option>
                {availableSubTopics.map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
              </select>
            </div>

            {/* 8. Level */}
            <div>
              <label style={filterLabelStyle}>Difficulty Level</label>
              <select
                value={levelFilter}
                onChange={e => { setLevelFilter(e.target.value); setCurrentPage(1); }}
                style={filterSelectStyle(Boolean(levelFilter))}
              >
                <option value="">All Levels</option>
                <option value="Any">Any</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Difficult">Difficult</option>
                <option value="Hot">Hot</option>
              </select>
            </div>

            {/* 9. Status */}
            <div>
              <label style={filterLabelStyle}>Status</label>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                style={filterSelectStyle(Boolean(statusFilter))}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ─── 3. Active Filter Chips Bar ─── */}
      {activeFilters.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 18,
          padding: '10px 14px',
          background: '#F8FAFC',
          borderRadius: 12,
          border: `1px solid ${C.g200}`
        }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.g500, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Filter size={13} /> Active Filters ({activeFilters.length}):
          </span>
          {activeFilters.map(af => (
            <span
              key={af.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: '#EEF2FF',
                color: C.primary,
                border: '1px solid #C7D2FE',
                borderRadius: 20,
                padding: '4px 10px',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span>{af.label}</span>
              <button
                type="button"
                onClick={af.clear}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: '#C7D2FE',
                  color: '#1E1B4B',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 10,
                  lineHeight: 1
                }}
                title="Remove filter"
              >
                <X size={11} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={handleResetAllFilters}
            style={{
              background: 'none',
              border: 'none',
              color: '#DC2626',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '2px 8px',
              textDecoration: 'underline'
            }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* ─── 4. Metric Stat Cards (Advansity Dashboard Style) - Placed below filters ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 20
      }}>
        <StatCard
          title="No. of Questions"
          value={boxTotalQuestions}
          subtitle={isAllFilterDefault ? "All in question bank" : "Questions in current filter"}
          icon={FileQuestion}
          color="#4F46E5"
          bg="#EEF2FF"
          border="#C7D2FE"
          isActive={!statusFilter}
          onClick={() => {
            if (statusFilter) {
              setStatusFilter('');
              setCurrentPage(1);
            } else if (!isAllFilterDefault) {
              handleResetAllFilters();
            }
          }}
          activeBadgeText={isAllFilterDefault ? "All Questions" : "Filtered Total"}
        />

        <StatCard
          title="Total Active"
          value={boxActiveQuestions}
          subtitle={`${boxTotalQuestions > 0 ? Math.round((boxActiveQuestions / boxTotalQuestions) * 100) : 100}% live in view`}
          icon={CheckCircle2}
          color="#10B981"
          bg="#ECFDF5"
          border="#A7F3D0"
          isActive={statusFilter === 'active'}
          onClick={() => {
            setStatusFilter(prev => prev === 'active' ? '' : 'active');
            setCurrentPage(1);
          }}
          activeBadgeText="Active Filter"
        />

        <StatCard
          title="Total Inactive"
          value={boxInactiveQuestions}
          subtitle={`${boxInactiveQuestions} archived in view`}
          icon={EyeOff}
          color="#EF4444"
          bg="#FEF2F2"
          border="#FECACA"
          isActive={statusFilter === 'inactive'}
          onClick={() => {
            setStatusFilter(prev => prev === 'inactive' ? '' : 'inactive');
            setCurrentPage(1);
          }}
          activeBadgeText="Inactive Filter"
        />

        <StatCard
          title="By Topics"
          value={boxTopicsCount}
          subtitle={`${boxTopicsCount} topic${boxTopicsCount !== 1 ? 's' : ''} in view`}
          icon={BookOpen}
          color="#8B5CF6"
          bg="#F5F3FF"
          border="#DDD6FE"
          isActive={Boolean(topicFilter)}
          onClick={() => {
            if (topicFilter) {
              setTopicFilter('');
              setCurrentPage(1);
            } else {
              setShowAdvanceFilters(true);
            }
          }}
          activeBadgeText="Topic Active"
        />

        <StatCard
          title="By Sub Topics"
          value={boxSubTopicsCount}
          subtitle={`${boxSubTopicsCount} sub topic${boxSubTopicsCount !== 1 ? 's' : ''} in view`}
          icon={Layers}
          color="#F59E0B"
          bg="#FFFBEB"
          border="#FDE68A"
          isActive={Boolean(subTopicFilter)}
          onClick={() => {
            if (subTopicFilter) {
              setSubTopicFilter('');
              setCurrentPage(1);
            } else {
              setShowAdvanceFilters(true);
            }
          }}
          activeBadgeText="Sub Topic Active"
        />
      </div>

      {/* ─── Bulk Action Banner (Visible when items are selected) ─── */}
      {selectedIds.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
          border: '1px solid #C7D2FE',
          borderRadius: 14,
          padding: '14px 20px',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: C.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckSquare size={18} />
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1E1B4B' }}>
                {selectedIds.length} question{selectedIds.length > 1 ? 's' : ''} selected
              </span>
              {filtered.length > paginated.length && selectedIds.length === manageablePaginated.length && (
                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  style={{ marginLeft: 12, background: 'none', border: 'none', color: C.primary, fontWeight: 600, fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                >
                  Select all {filtered.filter(canManageQuestion).length} questions matching filters
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Deactivate button */}
            <button
              type="button"
              onClick={() => handleBulkStatus('inactive')}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#FFFBEB',
                color: '#B45309',
                border: '1px solid #FCD34D',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              title="Deactivate selected questions (hide from quizzes)"
            >
              <EyeOff size={15} /> Deactivate Selected
            </button>

            {/* Activate button */}
            <button
              type="button"
              onClick={() => handleBulkStatus('active')}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#ECFDF5',
                color: '#059669',
                border: '1px solid #A7F3D0',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              title="Activate selected questions"
            >
              <CheckCircle2 size={15} /> Activate Selected
            </button>

            {/* Delete button */}
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#DC2626',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                transition: 'all 0.15s',
              }}
              title="Permanently delete selected questions"
            >
              <Trash2 size={15} /> Delete Selected
            </button>

            {/* Deselect */}
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={bulkLoading}
              style={{
                padding: '8px 12px',
                background: '#fff',
                color: C.g600,
                border: `1px solid ${C.g200}`,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* ─── Question Table ─── */}
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                <th style={{ padding: '16px 14px', width: 44, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    ref={el => { if (el) el.indeterminate = isSomePageSelected; }}
                    onChange={handleSelectAllPage}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: C.primary }}
                    title="Select all on this page"
                  />
                </th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>ID</th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', width: '28%' }}>Question</th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Classification</th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Topic / Sub Topic</th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Level / Ans</th>
                <th style={{ padding: '16px 16px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>Loading questions...</td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>No questions found</td></tr>
              ) : (
                paginated.map(q => {
                  const isChecked = selectedIds.includes(q.id);
                  const isInactive = q.status === 'inactive';
                  const isManageable = canManageQuestion(q);
                  return (
                    <tr
                      key={q.id}
                      style={{
                        borderBottom: `1px solid ${C.g100}`,
                        background: isChecked ? '#F5F7FF' : 'transparent',
                        transition: 'background 0.15s ease',
                      }}
                    >
                      {/* Checkbox column */}
                      <td style={{ padding: '16px 14px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          disabled={!isManageable}
                          onChange={() => handleToggleSelect(q.id)}
                          style={{
                            width: 16,
                            height: 16,
                            cursor: isManageable ? 'pointer' : 'not-allowed',
                            accentColor: C.primary,
                          }}
                          title={isManageable ? 'Select question' : 'You can only select questions you uploaded or created'}
                        />
                      </td>

                      {/* ID column */}
                      <td style={{ padding: '16px 16px', fontSize: 13, color: C.g500, fontWeight: 500 }}>
                        #{q.id}
                      </td>

                      {/* Question Text */}
                      <td style={{ padding: '16px 16px', fontSize: 14, fontWeight: 500, color: C.g900 }}>
                        <div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.45 }}>
                          {q.question_text}
                        </div>
                      </td>

                      {/* Classification */}
                      <td style={{ padding: '16px 16px', fontSize: 13, color: C.g600 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span style={{ fontWeight: 600, color: C.g800 }}>{q.subject_name || '-'}</span>
                          <span style={{ fontSize: 11, color: C.g500 }}>{q.exam_name || q.board_name || '-'} | {q.class_name || '-'}</span>
                        </div>
                      </td>

                      {/* Topic / Sub Topic */}
                      <td style={{ padding: '16px 16px', fontSize: 13, color: C.g600 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                          <span style={{ display: 'inline-block', padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 4, fontWeight: 600 }}>
                            {q.topic_name || '-'}
                          </span>
                          <span style={{ display: 'inline-block', padding: '2px 8px', background: C.g100, color: C.g700, borderRadius: 4, fontSize: 11 }}>
                            {q.sub_topic_name || '-'}
                          </span>
                        </div>
                      </td>

                      {/* Level / Answer */}
                      <td style={{ padding: '16px 16px', fontSize: 13, color: C.g600 }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', background: C.g100, borderRadius: 4, marginBottom: 4 }}>
                          {q.level || 'Any'}
                        </span>
                        <br/>
                        <strong style={{ color: C.primary }}>Opt: {q.correct_answer}</strong>
                      </td>

                      {/* Status Column */}
                      <td style={{ padding: '16px 16px', fontSize: 13 }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '3px 9px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                          background: isInactive ? '#FEF2F2' : '#ECFDF5',
                          color: isInactive ? '#DC2626' : '#059669',
                          border: `1px solid ${isInactive ? '#FECACA' : '#A7F3D0'}`,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: isInactive ? '#EF4444' : '#10B981' }} />
                          {isInactive ? 'Inactive' : 'Active'}
                        </span>
                      </td>

                      {/* Actions Column */}
                      <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            onClick={() => setViewQuestion(q)}
                            style={{ padding: 6, background: '#EFF6FF', border: 'none', borderRadius: 6, color: '#3B82F6', cursor: 'pointer' }}
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>

                          {isManageable ? (
                            <>
                              {/* Quick Toggle Status */}
                              <button
                                onClick={() => handleToggleStatus(q)}
                                style={{
                                  padding: 6,
                                  background: isInactive ? '#ECFDF5' : '#FFFBEB',
                                  border: 'none',
                                  borderRadius: 6,
                                  color: isInactive ? '#059669' : '#D97706',
                                  cursor: 'pointer'
                                }}
                                title={isInactive ? 'Activate question' : 'Deactivate question'}
                              >
                                {isInactive ? <CheckCircle2 size={16} /> : <EyeOff size={16} />}
                              </button>

                              <button
                                onClick={() => setEditQuestion({ ...q })}
                                style={{ padding: 6, background: '#FEF3C7', border: 'none', borderRadius: 6, color: '#D97706', cursor: 'pointer' }}
                                title="Edit"
                              >
                                <Edit2 size={16} />
                              </button>

                              <button
                                onClick={() => handleDelete(q.id)}
                                style={{ padding: 6, background: '#FEF2F2', border: 'none', borderRadius: 6, color: C.danger, cursor: 'pointer' }}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: C.g500, background: C.g100, padding: '4px 10px', borderRadius: 12, fontWeight: 500 }} title="You can only delete or edit questions you uploaded or created">
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Records Per Page Bar */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${C.g200}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          background: '#fff'
        }}>
          {/* Records per page & info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: C.g600, fontWeight: 500 }}>Records per page:</span>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.g800,
                  background: '#fff',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span style={{ fontSize: 13, color: C.g500 }}>
              Showing {filtered.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length} entries
            </span>
          </div>

          {/* Pagination Navigation */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* First Page button */}
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="First page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === 1 ? C.g300 : C.g700,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsLeft size={16} />
              </button>

              {/* Previous button */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Previous page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === 1 ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft size={16} /> Prev
              </button>

              {/* Page numbers with ellipsis */}
              {getPaginationItems().map((item, index) => {
                if (item === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      style={{
                        width: 28,
                        textAlign: 'center',
                        color: C.g400,
                        fontSize: 13,
                        fontWeight: 600,
                        userSelect: 'none'
                      }}
                    >
                      …
                    </span>
                  );
                }

                const isCurrent = item === currentPage;
                return (
                  <button
                    key={`page-${item}`}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    style={{
                      minWidth: 34,
                      height: 34,
                      padding: '0 8px',
                      borderRadius: 8,
                      border: `1px solid ${isCurrent ? C.primary : C.g200}`,
                      background: isCurrent ? C.primary : '#fff',
                      color: isCurrent ? '#fff' : C.g700,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {item}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Next page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === totalPages ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next <ChevronRight size={16} />
              </button>

              {/* Last Page button */}
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === totalPages ? C.g300 : C.g700,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* ─── View Modal ─── */}
      {viewQuestion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 700, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h3 style={{ margin: 0, fontSize: 18, color: C.g900, fontWeight: 700 }}>Question Details</h3>
                <span style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 12,
                  fontWeight: 700,
                  background: viewQuestion.status === 'inactive' ? '#FEF2F2' : '#ECFDF5',
                  color: viewQuestion.status === 'inactive' ? '#DC2626' : '#059669',
                  border: `1px solid ${viewQuestion.status === 'inactive' ? '#FECACA' : '#A7F3D0'}`
                }}>
                  {(viewQuestion.status || 'active').toUpperCase()}
                </span>
              </div>
              <button onClick={() => setViewQuestion(null)} style={{ background: 'none', border: 'none', color: C.g500, cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 6 }} onMouseOver={e => e.currentTarget.style.background = C.g200} onMouseOut={e => e.currentTarget.style.background = 'none'}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: 24, overflowY: 'auto' }}>
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, padding: '4px 10px', background: C.g100, color: C.g700, borderRadius: 20, fontWeight: 600 }}>ID: #{viewQuestion.id}</span>
                <span style={{ fontSize: 12, padding: '4px 10px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 20, fontWeight: 600 }}>{viewQuestion.exam_name || viewQuestion.board_name || 'No Board/Exam'}</span>
                <span style={{ fontSize: 12, padding: '4px 10px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 20, fontWeight: 600 }}>{viewQuestion.class_name || 'No Class'}</span>
                <span style={{ fontSize: 12, padding: '4px 10px', background: '#F5F3FF', color: '#6D28D9', borderRadius: 20, fontWeight: 600 }}>{viewQuestion.subject_name || 'No Subject'}</span>
                <span style={{ fontSize: 12, padding: '4px 10px', background: '#FEF2F2', color: '#B91C1C', borderRadius: 20, fontWeight: 600 }}>{viewQuestion.level}</span>
                <span style={{ fontSize: 12, padding: '4px 10px', background: '#FFFBEB', color: '#B45309', borderRadius: 20, fontWeight: 600 }}>{viewQuestion.format || 'MCQ'}</span>
              </div>
              
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 12, color: C.g500, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Question</div>
                <div style={{ fontSize: 16, color: C.g900, fontWeight: 500, lineHeight: 1.5 }}>{viewQuestion.question_text}</div>
              </div>

              {!['Subjective', 'Practical'].includes(viewQuestion.format) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                  {['A', 'B', 'C', 'D'].map(opt => (
                    <div key={opt} style={{ padding: 12, borderRadius: 10, border: `2px solid ${viewQuestion.correct_answer === opt ? C.primary : C.g200}`, background: viewQuestion.correct_answer === opt ? '#EEF2FF' : '#fff', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 12, background: viewQuestion.correct_answer === opt ? C.primary : C.g200, color: viewQuestion.correct_answer === opt ? '#fff' : C.g700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{opt}</div>
                      <div style={{ fontSize: 14, color: C.g800, marginTop: 2 }}>{viewQuestion[`option_${opt.toLowerCase()}`]}</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: C.g500, fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Topic / Sub Topic</div>
                <div style={{ fontSize: 14, color: C.g800 }}>{viewQuestion.topic_name || '-'} {viewQuestion.sub_topic_name ? `> ${viewQuestion.sub_topic_name}` : ''}</div>
              </div>

              {viewQuestion.answer_desc && (
                <div style={{ padding: 16, background: '#F0FDF4', borderRadius: 12, border: '1px solid #BBF7D0' }}>
                  <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6 }}>Explanation</div>
                  <div style={{ fontSize: 14, color: '#15803D', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: viewQuestion.answer_desc }} />
                </div>
              )}
            </div>
            
            <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.g100}`, background: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setViewQuestion(null)} style={{ padding: '10px 20px', background: C.g100, color: C.g800, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Edit Modal ─── */}
      {editQuestion && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 700, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: `1px solid ${C.g100}`, background: C.g50 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: C.g900, fontWeight: 700 }}>Edit Question</h3>
              <button onClick={() => setEditQuestion(null)} style={{ background: 'none', border: 'none', color: C.g500, cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 6 }} onMouseOver={e => e.currentTarget.style.background = C.g200} onMouseOut={e => e.currentTarget.style.background = 'none'}>
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleUpdateQuestion} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: 24, overflowY: 'auto' }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Text</label>
                  <textarea value={editQuestion.question_text} onChange={e => setEditQuestion({...editQuestion, question_text: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: 8, border: `1px solid ${C.g200}`, minHeight: 80, outline: 'none' }} required />
                </div>

                {!['Subjective', 'Practical'].includes(editQuestion.format) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option A</label><input type="text" value={editQuestion.option_a} onChange={e => setEditQuestion({...editQuestion, option_a: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option B</label><input type="text" value={editQuestion.option_b} onChange={e => setEditQuestion({...editQuestion, option_b: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option C</label><input type="text" value={editQuestion.option_c} onChange={e => setEditQuestion({...editQuestion, option_c: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                    <div><label style={{ fontSize: 12, color: C.g600 }}>Option D</label><input type="text" value={editQuestion.option_d} onChange={e => setEditQuestion({...editQuestion, option_d: e.target.value})} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.g200}` }} required /></div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Format</label>
                    <select value={editQuestion.format || 'MCQ'} onChange={e => setEditQuestion({...editQuestion, format: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="MCQ">MCQ</option><option value="Subjective">Subjective</option><option value="Practical">Practical</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Difficulty Level</label>
                    <select value={editQuestion.level} onChange={e => setEditQuestion({...editQuestion, level: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="Any">Any</option><option value="Easy">Easy</option><option value="Medium">Medium</option><option value="Difficult">Difficult</option><option value="Hot">Hot</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Status</label>
                    <select value={editQuestion.status || 'active'} onChange={e => setEditQuestion({...editQuestion, status: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="active">Active</option><option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {!['Subjective', 'Practical'].includes(editQuestion.format) && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Correct Answer</label>
                    <select value={editQuestion.correct_answer} onChange={e => setEditQuestion({...editQuestion, correct_answer: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${C.g200}`, background: '#fff' }}>
                      <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: 40 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Answer Description (Optional)</label>
                  <ReactQuill theme="snow" value={editQuestion.answer_desc || ''} onChange={val => setEditQuestion({...editQuestion, answer_desc: val})} style={{ background: '#fff', height: 150 }} />
                </div>
              </div>
              
              <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.g100}`, background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button type="button" onClick={() => setEditQuestion(null)} style={{ padding: '10px 20px', background: C.g100, color: C.g800, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default QuestionList;
